import { ReauthField, useReauth } from '@felix/ui';
import { Alert, Button, CopyButton, Group, Modal, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClockHour4,
  IconCopy,
  IconKey,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi, type SetupCode } from '../api.js';
import { ErrorAlert } from './ErrorAlert.js';
import classes from './SetupCodeModal.module.css';

interface Props {
  vessel: { id: string; name: string; activated: boolean } | null;
  onClose: () => void;
}

/**
 * Gemi sunucusu kurulum kodu (mimari belge §5.2): bir kez gösterilir. Kod ancak kullanıcı
 * açıkça isteyince oluşturulur; etkin sunucusu olan gemide yeni sunucu eskisini devreden
 * çıkardığı için önce uyarı gösterilir (§5.5). Kritik işlemdir: işlemi yapanın parolası her
 * zaman istenir (türsüz `useReauth`; ADR 0075 §5, ADR 0151 §2).
 */
export function SetupCodeModal({ vessel, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState<SetupCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reauth = useReauth();
  const resetPassword = reauth.reset;

  useEffect(() => {
    if (vessel === null) {
      setCode(null);
      setError(null);
      resetPassword();
    }
  }, [vessel, resetPassword]);

  const create = async () => {
    if (!vessel || reauth.missing) return;
    setBusy(true);
    setError(null);
    try {
      setCode(await officeApi.createSetupCode(vessel.id, reauth.password));
      // Parola yalnız bu istek için: kod gösterilince alan boşalır
      resetPassword();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
    } finally {
      setBusy(false);
    }
  };

  const validUntil = code
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(code.expiresAt))
    : '';

  return (
    <Modal
      opened={vessel !== null}
      onClose={onClose}
      title={vessel ? t('fleet.code.title', { vessel: vessel.name }) : ''}
      centered
      size="lg"
      radius="md"
      closeButtonProps={{ 'aria-label': t('fleet.closeDialog') }}
    >
      <Stack gap="md">
        <Text fz="sm" c="dimmed">
          {t('fleet.code.intro')}
        </Text>
        {vessel?.activated && !code ? (
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
            {t('fleet.code.replaceWarning')}
          </Alert>
        ) : null}
        <ErrorAlert code={error} />

        {code ? (
          <>
            <div className={classes.codeBox}>
              <Text fz="xs" fw={600} className={classes.codeLabel}>
                {t('fleet.code.label')}
              </Text>
              <output className={classes.code} aria-label={t('fleet.code.label')}>
                {code.code}
              </output>
              <CopyButton value={code.code} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    variant={copied ? 'light' : 'filled'}
                    color={copied ? 'teal' : 'brand'}
                    leftSection={copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                    onClick={copy}
                  >
                    {copied ? t('fleet.code.copied') : t('fleet.code.copy')}
                  </Button>
                )}
              </CopyButton>
            </div>
            <Text fz="sm" className={classes.meta}>
              <IconClockHour4 size={16} aria-hidden />
              {t('fleet.code.validUntil', { date: validUntil })}
            </Text>
            <Text fz="sm">{t('fleet.code.next')}</Text>
            <Text fz="xs" c="dimmed">
              {t('fleet.code.onceOnly')}
            </Text>
            <Group justify="flex-end">
              <Button onClick={onClose}>{t('fleet.code.close')}</Button>
            </Group>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            <ReauthField
              reauth={reauth}
              label={t('fleet.code.password')}
              description={t('fleet.code.passwordHelp')}
              mb="md"
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={onClose}>
                {t('fleet.code.cancel')}
              </Button>
              <Button
                type="submit"
                loading={busy}
                disabled={reauth.missing}
                color={vessel?.activated ? 'red' : 'brand'}
                leftSection={<IconKey size={18} />}
              >
                {t('fleet.code.create')}
              </Button>
            </Group>
          </form>
        )}
      </Stack>
    </Modal>
  );
}
